import { useProfessionalEditionGuardedImport } from '@common/credits/ProfessionalEdition/hooks';
import { normalizeStringForTestId } from '@common/utils';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { ArrowNarrowLeftIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, Dialog, Stack } from '@prophecy/ui-v3';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { downloadAnalysisReport } from 'frontend/core/src/data/apis/api';
import { useState } from 'react';
import styled from 'styled-components';

import { errorHandler } from '../../../../data/util';
import { FileFormats } from '../../config';
import { ImportStepValue } from '../steps/types';
import { AnalysisAction } from '../types';
import { useImportState } from '../useImportState';
import { isInformatica } from '../utils';

type Props = {
  onClick: () => void;
  disabled?: boolean;
  btnText?: string;
  isLoading?: boolean;
};

const UnderlinedText = styled.span`
  text-decoration: underline;
`;

const StyledButton = styled(Button)`
  padding: 0;
` as unknown as typeof Button;
const associatedEntitiesLoadingTooltip = 'Please wait for the associated entities to load.';
const isAnalysisInProgressTooltip = 'Please wait for the analysis to complete.';

export const ImportFooter = ({ onClick, isLoading, disabled, btnText }: Props) => {
  const guardImport = useProfessionalEditionGuardedImport();
  const {
    isDeleting,
    transpileGraphState: {
      clientState: {
        analysisSummary: { analysisAction, canDownload },
        transpileSummary: { isSuccess, isError, hasCancelEnabled },
        upload: { isUploading },
        validate: { isCancelling, validatedData },
        savedEntities: { associatedEntitiesLoading }
      },
      actions: { startAnalysis, cancelTranspilation, cancelAnalysis, startValidation, startTranspilation }
    },
    transpilerState: {
      transpilerId,
      isUpdate,
      activeStep,
      transpilerEntityTypes: { pipelines, jobs }
    },
    loading,
    actions: { handleStepChange },
    formDataWithActions: {
      formContext: { watch }
    }
  } = useImportState();

  const source = watch('source');
  const language = watch('language');
  const isScala = language === BackEndCodeLanguage.scala;
  const isSql = language === BackEndCodeLanguage.sql;

  const { isAnalyzerEnabledForInformatica } = useGlobalVariables();

  const isChooseEntityStep = activeStep === ImportStepValue.ChooseEntities;
  const isTranspileGraph = activeStep === ImportStepValue.TranspileGraph;
  const hasNotPreviousStep = activeStep === ImportStepValue.Sources || activeStep > ImportStepValue.TranspileGraph;
  const hasFileEntites = jobs?.length > 0 || pipelines?.length > 0;
  const isDisabled = !hasFileEntites || loading || disabled;

  const isAnalysing = analysisAction === AnalysisAction.transpiling;
  const isAnalyzeStep = activeStep === ImportStepValue.AnalyzeGraph;
  const hasAnalysis =
    source === FileFormats.alteryx
      ? !isScala
      : isInformatica(source as FileFormats) && isAnalyzerEnabledForInformatica
        ? isSql
        : false;

  const isTranspiling = isTranspileGraph && !isSuccess && !isError;
  const isUpdateStep = Boolean(isUpdate && isChooseEntityStep && !validatedData);
  const btnProps = btnText
    ? {
        onClick: () => (isChooseEntityStep ? guardImport(startTranspilation)() : onClick()),
        children: btnText,
        loading: Boolean(loading || isLoading || isUploading),
        disabled: Boolean(disabled) || isAnalysing || isUpdateStep || isDeleting || associatedEntitiesLoading,
        tooltip: associatedEntitiesLoading
          ? associatedEntitiesLoadingTooltip
          : isUpdateStep
            ? 'Validate graph to enable this option.'
            : isAnalysing
              ? isAnalysisInProgressTooltip
              : undefined,
        'data-test-id': `import-footer-${normalizeStringForTestId(btnText)}-button`
      }
    : undefined;

  const handleCancel = async () => {
    const deleteResp = await Dialog.warning({
      title: 'Stop Transpiling?',
      subTitle: 'Transpilation is still in progress. Do you want to continue?',
      'data-test-id': 'stop-transpiling-dialog'
    });
    if (deleteResp === Dialog.ResponseAction.okButton) {
      cancelTranspilation();
    }
  };

  const initiateAnalysis = () => {
    if (!isAnalysing) {
      startValidation(true);
    } else {
      startAnalysis();
      handleStepChange(ImportStepValue.AnalyzeGraph);
    }
  };

  const [isDownloading, setIsLoading] = useState(false);
  const downloadReport = usePersistentCallback(async () => {
    setIsLoading(true);
    try {
      await downloadAnalysisReport({ transpilerId });
    } catch (error) {
      errorHandler('Please regenerate the analysis report.');
    } finally {
      setIsLoading(false);
    }
  });

  const getBtnTooltip = usePersistentCallback(() => {
    if (isDisabled) {
      return 'Please select entities to analyze.';
    }
    if (associatedEntitiesLoading) {
      return associatedEntitiesLoadingTooltip;
    }
    return undefined;
  });

  return (
    <Stack
      width='100%'
      padding={`${theme.spaces.x20}`}
      align={hasNotPreviousStep && !isTranspileGraph ? 'end' : 'space-between'}
      alignY='center'
      direction='horizontal'>
      {hasNotPreviousStep ? null : (
        <Button
          onClick={() => handleStepChange(Math.ceil(activeStep - 1), true)}
          iconPlacement='left'
          disabled={loading || (isTranspiling && (hasCancelEnabled || isCancelling))}
          variant='secondaryGray'
          icon={<ArrowNarrowLeftIcon type='default' />}
          data-test-id='import-footer-back-button'>
          Back
        </Button>
      )}
      <Stack direction='horizontal' gap={theme.spaces.x12}>
        {isChooseEntityStep && (
          <>
            {hasAnalysis && (
              <>
                {isAnalysing && (
                  <StyledButton
                    onClick={initiateAnalysis}
                    variant='linkGray'
                    data-test-id='import-footer-analysis-in-progress-button'>
                    <UnderlinedText>Analysis in progress</UnderlinedText>
                  </StyledButton>
                )}
                {canDownload && !isAnalysing && (
                  <StyledButton
                    onClick={downloadReport}
                    variant='linkGray'
                    loading={isDownloading}
                    data-test-id='import-footer-download-analysis-report-button'>
                    <UnderlinedText>Download Analysis Report</UnderlinedText>
                  </StyledButton>
                )}
                <Button
                  loading={isAnalysing}
                  disabled={isAnalysing || isDisabled || associatedEntitiesLoading}
                  tooltip={isAnalysing ? isAnalysisInProgressTooltip : getBtnTooltip()}
                  variant='secondaryGray'
                  onClick={initiateAnalysis}
                  data-test-id='import-footer-analyze-button'>
                  Analyze
                </Button>
              </>
            )}
            <Button
              disabled={isDisabled || associatedEntitiesLoading}
              tooltip={getBtnTooltip()}
              variant='secondaryGray'
              onClick={() => startValidation()}
              data-test-id='import-footer-validate-button'>
              Validate
            </Button>
          </>
        )}
        {isTranspiling && (hasCancelEnabled || isCancelling) && (
          <Button
            loading={isCancelling}
            variant='secondaryGray'
            onClick={handleCancel}
            data-test-id='import-footer-cancel-button'>
            {isCancelling ? 'Cancelling..' : 'Cancel'}
          </Button>
        )}
        {isAnalyzeStep && isAnalysing && (
          <Button
            variant='secondaryGray'
            onClick={() => {
              cancelAnalysis();
              handleStepChange(Math.ceil(ImportStepValue.AnalyzeGraph - 1));
            }}
            data-test-id='import-footer-cancel-analysis-button'>
            Cancel
          </Button>
        )}
        {btnProps && <Button {...btnProps} variant='primary' />}
      </Stack>
    </Stack>
  );
};
